import React, { MouseEvent, useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import classNames from 'classnames';

import { MODALS } from './types';
import { modalClose } from '@/store/modal/actions';
import { selectModalIsOpen, selectModals } from '@/store/modal/selectors';
import { cleanupModalHistoryEntry, handlePopstateClose } from './modalHistoryState';

export const ModalContainer = () => {
  const dispatch = useDispatch();
  const modals = useSelector(selectModals);
  const isOpen = useSelector(selectModalIsOpen);

  const closeModal = useCallback(
    (modal?: MODALS) => {
      dispatch(modalClose(modal));
    },
    [dispatch],
  );
  const modalWrapperRef = useRef<HTMLDivElement>(null);
  const isPopstateCloseRef = useRef(false);
  const closeModalCallbackRef = useRef<() => void>(() => {});

  const modal = modals[modals.length - 1];
  const props = modal?.props;

  const closeModalWithCallback = useCallback(() => {
    if (props?.onClose) {
      props.onClose();
    }
    if (!isPopstateCloseRef.current && modal?.name !== undefined) {
      cleanupModalHistoryEntry(modal.name);
    }
    closeModal(modal?.name);
  }, [props, modal?.name, closeModal]);

  useEffect(() => {
    closeModalCallbackRef.current = closeModalWithCallback;
  }, [closeModalWithCallback]);

  /*
   * This useEffect listens for browser back button (popstate) to close modals
   * */
  useEffect(() => {
    const handlePopstate = () => {
      const shouldClose = handlePopstateClose();
      if (shouldClose) {
        isPopstateCloseRef.current = true;
        closeModalCallbackRef.current();
        isPopstateCloseRef.current = false;
      }
    };

    window.addEventListener('popstate', handlePopstate);
    return () => window.removeEventListener('popstate', handlePopstate);
  }, []);

  /*
   * This useEffect is used for making <body> not scrollable when modals container is opened
   * */
  useEffect(() => {
    document.body.style.overflowY = isOpen ? 'hidden' : 'visible';
  }, [isOpen]);

  /*
   * This useEffect is used for handling of modals container backdrop click
   * */
  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleOnClickOutside = (event: MouseEvent | TouchEvent | FocusEvent) => {
      if (modalWrapperRef.current && !modalWrapperRef.current.contains(event.target as Node)) {
        if (isOpen && props && !props.noBackdrop && !props.noBackdropClick) {
          props.onBackdropClick?.();
          closeModalWithCallback();
        }
      }
    };

    document.addEventListener('mousedown', handleOnClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleOnClickOutside);
    };
  }, [isOpen, props, closeModalWithCallback]);

  if (modal) {
    const Modal = modal.component;

    return (
      <div
        className={classNames(
          `fixed top-0 bottom-0 right-0 left-0 z-[120] p-6 transition-opacity duration-150 ease-in-out flex
          justify-center items-center bg-black/80`,
          isOpen ? 'opacity-100' : 'opacity-0 h-0',
        )}
      >
        <div
          ref={modalWrapperRef}
          role="dialog"
          className="max-h-full no-scrollbar overflow-y-scroll"
        >
          {/*@ts-ignore*/}
          <Modal closeModal={closeModalWithCallback} modalWrapperRef={modalWrapperRef} {...props} />
        </div>
      </div>
    );
  }

  return null;
};
